/* Smart Flow Services - the client deal-room.
   A second space behind the server hall: dark NYC-boardroom mood with a
   skyline window, a long deal table with silhouette figures, and a glowing
   results screen. Also exports buildDoorway - the glowing door at the end
   of the hall's center aisle that leads here.
   All geometry is primitives + canvas textures (no external assets). */

/** Current local hour in Israel (DST handled by the Intl timezone). */
function israelHour() {
  return Number(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Jerusalem",
      hour: "2-digit",
      hour12: false,
    }).format(new Date()),
  );
}

/** Rough day/night rule for the boardroom windows: day 06:00–19:00 local. */
function isDayInIsrael() {
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    if (params.get("night") === "1" || params.get("time") === "night")
      return false;
    if (params.get("day") === "1" || params.get("time") === "day") return true;
  }
  const h = israelHour();
  return h >= 6 && h < 19;
}

/** Build a window sky texture, day or night, matching the plane's aspect
    ratio so round details stay round. Day: light horizon at the bottom
    rising to deep blue at the top, plus a soft sun glow near the horizon.
    Night: dark gradient with twinkling stars (no city, no moon).
    The canvas is sized to planeW/planeH, so stars are plain circles.
    starSize scales the stars - bigger for small (near-distance) windows so
    they stay visible when the texture is stretched thin; count lets small
    windows scatter fewer, chunky stars instead of a wash. */
function makeSkyTexture(
  THREE,
  planeW,
  planeH,
  day,
  starSize = 0.6,
  count = 140,
  sunRays = false,
) {
  const CW = 1024;
  const CH = Math.max(64, Math.round((CW * planeH) / planeW)); // match plane aspect
  const c = document.createElement("canvas");
  c.width = CW;
  c.height = CH;
  const g = c.getContext("2d");

  // ponytail: drawn ONCE per state flip (day/night), never per frame. The
  // old 10Hz redraws dropped the site to 12 FPS; twinkle now lives on a
  // cheap overlay plane whose opacity pulses (see makeTwinkleTexture).
  const sky = g.createLinearGradient(0, day ? CH : 0, 0, day ? 0 : CH);
  if (day) {
    sky.addColorStop(0, "#46B3FF"); // bright azure horizon, bottom
    sky.addColorStop(0.5, "#1B78E8"); // vivid blue
    sky.addColorStop(1, "#003EC2"); // deep electric blue, top
    g.fillStyle = sky;
    g.fillRect(0, 0, CW, CH);
    if (sunRays) {
      // Sun just below the bottom edge of the window: the disc itself is
      // outside the canvas (below the frame). Only a soft warm glow rises
      // up from under the sill - no hard ray wedges.
      g.globalCompositeOperation = "lighter";
      const sunX = CW * 0.5,
        sunY = CH * 1.15;
      const glow = g.createRadialGradient(sunX, sunY, 0, sunX, sunY, CH * 1.3);
      glow.addColorStop(0, "rgba(255,238,190,0.5)");
      glow.addColorStop(0.4, "rgba(255,236,185,0.18)");
      glow.addColorStop(1, "rgba(255,236,185,0)");
      g.fillStyle = glow;
      g.fillRect(0, 0, CW, CH);
      g.globalCompositeOperation = "source-over";
    }
  } else {
    sky.addColorStop(0, "#050a12");
    sky.addColorStop(1, "#0d1722");
    g.fillStyle = sky;
    g.fillRect(0, 0, CW, CH);
    for (let i = 0; i < count; i++) {
      const a = 0.25 + Math.random() * 0.65;
      const r = Math.max(0.35, (0.4 + Math.random() * 0.5) * starSize);
      g.fillStyle = `rgba(255,255,255,${(a * 0.85).toFixed(3)})`;
      g.beginPath();
      g.arc(Math.random() * CW, Math.random() * CH, r, 0, Math.PI * 2);
      g.fill();
    }
  }

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return { tex };
}

/** A volumetric light shaft (god ray) - a soft additive cone that stands
    like a beam of light spilling through a window during the day. Additive
    blending + low opacity reads as volumetric light against the dark room.
    Returns a mesh; scene.js hides it at night (see propsUpdate). */
function makeLightShaft(THREE, len) {
  const mat = new THREE.MeshBasicMaterial({
    color: 0xfff4dd,
    transparent: true,
    opacity: 0.05,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    toneMapped: false,
    side: THREE.DoubleSide,
  });
  const geo = new THREE.CylinderGeometry(0.3, 1.6, len, 24, 1, true);
  const shaft = new THREE.Mesh(geo, mat);
  return shaft;
}

/** A handful of bright stars on a transparent canvas, drawn once. The plane
    using it sits just in front of the static sky and only its material
    opacity pulses - a GPU-free twinkle with zero per-frame canvas work. */
function makeTwinkleTexture(THREE, planeW, planeH, starSize = 0.6, count = 24) {
  const CW = 256;
  const CH = Math.max(32, Math.round((CW * planeH) / planeW));
  const c = document.createElement("canvas");
  c.width = CW;
  c.height = CH;
  const g = c.getContext("2d");
  for (let i = 0; i < count; i++) {
    g.fillStyle = "rgba(255,255,255,0.95)";
    g.beginPath();
    g.arc(
      Math.random() * CW,
      Math.random() * CH,
      Math.max(0.35, starSize),
      0,
      Math.PI * 2,
    );
    g.fill();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Draw the presentation screen to a canvas texture: dark chart, faint grid,
    baseline bars and one glowing cyan line climbing to the right. */
function makeScreenTexture(THREE) {
  const c = document.createElement("canvas");
  c.width = 512;
  c.height = 256;
  const g = c.getContext("2d");

  g.fillStyle = "#050a11";
  g.fillRect(0, 0, 512, 256);

  g.strokeStyle = "rgba(102,204,240,0.10)";
  g.lineWidth = 1;
  for (let i = 32; i < 512; i += 32) {
    g.beginPath();
    g.moveTo(i, 0);
    g.lineTo(i, 256);
    g.stroke();
  }
  for (let i = 32; i < 256; i += 32) {
    g.beginPath();
    g.moveTo(0, i);
    g.lineTo(512, i);
    g.stroke();
  }

  g.fillStyle = "rgba(102,204,240,0.22)";
  const bars = [38, 52, 46, 70, 64, 88, 96];
  bars.forEach((bh, i) => g.fillRect(48 + i * 60, 236 - bh, 22, bh));

  g.strokeStyle = "#66ccf0";
  g.lineWidth = 3;
  g.shadowColor = "#66ccf0";
  g.shadowBlur = 12;
  g.beginPath();
  const pts = [
    [30, 210],
    [110, 178],
    [180, 192],
    [250, 140],
    [320, 152],
    [390, 96],
    [482, 52],
  ];
  pts.forEach(([px, py], i) => (i ? g.lineTo(px, py) : g.moveTo(px, py)));
  g.stroke();

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Draw a clearly-placeholder project logo to a canvas texture for the
    boardroom presentation screen. Dashed border + diagonal PLACEHOLDER stamp
    so nobody mistakes the fake mark for a real brand asset. */
export function makeProjectLogoTexture(THREE, project) {
  const c = document.createElement("canvas");
  c.width = 640;
  c.height = 248;
  const g = c.getContext("2d");

  // Panel.
  g.fillStyle = "#070d15";
  g.fillRect(0, 0, 640, 248);

  // Dashed placeholder border.
  g.strokeStyle = "rgba(102,204,240,0.55)";
  g.lineWidth = 3;
  g.setLineDash([14, 10]);
  g.strokeRect(12, 12, 616, 224);

  // Diagonal PLACEHOLDER stamp.
  g.save();
  g.translate(320, 124);
  g.rotate(-0.42);
  g.fillStyle = "rgba(255,255,255,0.16)";
  g.font = '600 46px "JetBrains Mono", monospace';
  g.textAlign = "center";
  g.textBaseline = "middle";
  g.fillText("PLACEHOLDER", 0, 0);
  g.restore();

  // Monogram tile.
  const color = project.color || "#66ccf0";
  g.fillStyle = color;
  const tile = 96;
  g.beginPath();
  g.roundRect(74 - tile / 2, 124 - tile / 2, tile, tile, 18);
  g.fill();
  g.fillStyle = "#04080d";
  g.font = "800 44px Heebo, sans-serif";
  g.textAlign = "center";
  g.textBaseline = "middle";
  g.fillText(project.mark || "?", 74, 124);

  // Name + pending label.
  g.fillStyle = "#dceaf4";
  g.font = "600 30px Heebo, sans-serif";
  g.textAlign = "left";
  g.fillText(project.name, 150, 116);
  g.fillStyle = "rgba(220,234,244,0.5)";
  g.font = '400 18px "JetBrains Mono", monospace';
  g.fillText("LOGO PENDING", 150, 144);

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Fill word-wrapped text onto the canvas at (x, y), advancing by lineHeight
    per line. Returns the final y so callers can stack lines safely. */
function wrapText(g, text, x, y, maxWidth, lineHeight) {
  const words = String(text).split(/\s+/);
  let line = "";
  let yy = y;
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (line && g.measureText(test).width > maxWidth) {
      g.fillText(line, x, yy);
      line = w;
      yy += lineHeight;
    } else {
      line = test;
    }
  }
  if (line) g.fillText(line, x, yy);
  return yy;
}

/** Word-wrap `text` centered around cx, advancing lineHeight per line.
    Returns the final y. Caps at 2 lines so long names never overflow. */
function wrapCentered(g, text, cx, y, maxWidth, lineHeight) {
  const words = String(text).split(/\s+/);
  let line = "";
  const lines = [];
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (line && g.measureText(test).width > maxWidth) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  const n = Math.min(lines.length, 2);
  for (let i = 0; i < n; i++) {
    g.fillText(lines[i], cx, y + i * lineHeight);
  }
  return y + n * lineHeight;
}

/** Draw ONE client's board card: their logo tile + name on the left, and the
    recommendation they left (star rating + quote) next to it on the right.
    Matching the 8.2 x 3.3 screen aspect. `client` fields:
    { mark, color, stars, name, role, quote } - strings in the CURRENT UI
    language. */
export function makeClientReviewTexture(THREE, client) {
  const c = document.createElement("canvas");
  c.width = 1240;
  c.height = 500;
  const g = c.getContext("2d");

  // Panel.
  g.fillStyle = "#070d15";
  g.fillRect(0, 0, 1240, 500);

  // Dashed border.
  g.strokeStyle = "rgba(102,204,240,0.55)";
  g.lineWidth = 3;
  g.setLineDash([12, 9]);
  g.strokeRect(12, 12, 1216, 476);
  g.setLineDash([]);

  // Vertical divider: logo left, recommendation right.
  g.strokeStyle = "rgba(102,204,240,0.22)";
  g.lineWidth = 2;
  g.beginPath();
  g.moveTo(430, 96);
  g.lineTo(430, 404);
  g.stroke();

  // --- Left: logo tile + client name + role ---
  const color = client.color || "#66ccf0";
  const cx = 230;
  const tile = 150;
  g.fillStyle = color;
  g.beginPath();
  g.roundRect(cx - tile / 2, 108, tile, tile, 28);
  g.fill();
  g.fillStyle = "#04080d";
  g.font = "800 62px Heebo, sans-serif";
  g.textAlign = "center";
  g.textBaseline = "middle";
  g.fillText(client.mark || "?", cx, 108 + tile / 2 + 4);

  g.fillStyle = "#dceaf4";
  g.font = "600 30px Heebo, sans-serif";
  g.textBaseline = "alphabetic";
  let yy = wrapCentered(g, client.name || "", cx, 312, 380, 40);
  g.fillStyle = "rgba(220,234,244,0.5)";
  g.font = '400 17px "JetBrains Mono", monospace';
  g.fillText(client.role || "", cx, yy + 28);

  // --- Right: star rating + recommendation quote ---
  const stars = Math.max(1, Math.min(5, client.stars || 5));
  g.font = "34px Arial, sans-serif";
  g.textAlign = "left";
  for (let s = 0; s < stars; s++) {
    g.fillStyle = "#f5c542";
    g.fillText("\u2605", 490 + s * 46, 138);
  }
  g.fillStyle = "rgba(220,234,244,0.5)";
  g.font = '400 18px "JetBrains Mono", monospace';
  g.fillText(stars + ".0", 490 + stars * 46 + 10, 138);

  // Quote mark.
  g.fillStyle = "rgba(102,204,240,0.9)";
  g.font = "600 48px Georgia, serif";
  g.fillText("\u201C", 490, 192);

  // Quote text (word-wrapped).
  g.fillStyle = "#dceaf4";
  g.font = "400 23px Heebo, sans-serif";
  wrapText(g, client.quote || "", 490, 222, 690, 33);

  // Author pinned to the bottom.
  g.fillStyle = "#66ccf0";
  g.font = "600 24px Heebo, sans-serif";
  g.fillText("- " + (client.name || ""), 490, 442);

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Build one silhouette figure: capsule torso + sphere head, matte near-black.
    Faceless on purpose - they read as movie deal-room silhouettes, not avatars. */
function makeFigure(THREE, mat, seated) {
  const fig = new THREE.Group();
  const torsoLen = seated ? 0.55 : 0.85;
  const torso = new THREE.Mesh(
    new THREE.CapsuleGeometry(0.21, torsoLen, 4, 10),
    mat,
  );
  torso.position.y = seated ? 0.92 : 1.15;
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.155, 12, 10), mat);
  head.position.y = torso.position.y + torsoLen / 2 + 0.26;
  fig.add(torso, head);
  return fig;
}

/** The standing presenter figure - the same silhouette as the seated figures,
    plus one articulated right arm so it can point at the board. Returns
    { fig, arm } where arm is the shoulder pivot group the update loop rotates
    for the pointing gesture (arm hangs at the side at rest). */
function makePresenter(THREE, mat) {
  const fig = makeFigure(THREE, mat, false);
  const arm = new THREE.Group();
  arm.position.set(0.26, 1.52, 0); // right shoulder
  const upper = new THREE.Mesh(
    new THREE.CapsuleGeometry(0.07, 0.3, 4, 10),
    mat,
  );
  upper.position.y = -0.15; // hangs down from the shoulder
  arm.add(upper);
  fig.add(arm);
  return { fig, arm };
}

/** The glowing doorway at the end of the hall's center aisle.
    scene.js tags it userData.section = 'work' and makes it clickable. */
export function buildDoorway(THREE, ACCENT) {
  const g = new THREE.Group();
  const frameMat = new THREE.MeshStandardMaterial({
    color: 0x0a0e14,
    roughness: 0.55,
    metalness: 0.65,
  });

  for (const px of [-1.05, 1.05]) {
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.2, 3.4, 0.3), frameMat);
    post.position.set(px, 1.7, 0);
    g.add(post);
  }
  const lintel = new THREE.Mesh(new THREE.BoxGeometry(2.3, 0.2, 0.3), frameMat);
  lintel.position.set(0, 3.4, 0);
  g.add(lintel);

  // The opening itself: a translucent dark plane so the deal-room's light
  // spills through the doorway when you approach from the hall, framed by
  // glowing strips (bloom picks up).
  const voidPlane = new THREE.Mesh(
    new THREE.PlaneGeometry(1.9, 3.3),
    new THREE.MeshBasicMaterial({
      color: 0x010306,
      transparent: true,
      opacity: 0.25,
    }),
  );
  voidPlane.position.set(0, 1.65, -0.02);
  g.add(voidPlane);

  const glowMat = new THREE.MeshBasicMaterial({
    color: ACCENT,
    toneMapped: false,
  });
  for (const px of [-0.94, 0.94]) {
    const strip = new THREE.Mesh(
      new THREE.BoxGeometry(0.035, 3.3, 0.035),
      glowMat,
    );
    strip.position.set(px, 1.65, 0.1);
    g.add(strip);
  }
  const topStrip = new THREE.Mesh(
    new THREE.BoxGeometry(1.92, 0.035, 0.035),
    glowMat,
  );
  topStrip.position.set(0, 3.28, 0.1);
  g.add(topStrip);

  // Cyan light spill on the floor in front of the door.
  const spill = new THREE.Mesh(
    new THREE.PlaneGeometry(2.4, 1.6),
    new THREE.MeshBasicMaterial({
      color: ACCENT,
      transparent: true,
      opacity: 0.07,
      toneMapped: false,
    }),
  );
  spill.rotation.x = -Math.PI / 2;
  spill.position.set(0, 0.012, 0.9);
  g.add(spill);

  return g;
}

/** The full deal-room. Returns { group, anchor } - anchor is the focus-light
    target (center of the deal table). Room center sits at z = -55, far behind
    the hall, so the hall fog hides it until scene.js swaps fog at the fade. */
export function buildBoardroom(THREE, ACCENT) {
  const group = new THREE.Group();
  const CZ = -50; // room center z
  const W = 29.6,
    D = 29.6,
    H = 11.6;

  const wallMat = new THREE.MeshStandardMaterial({
    color: 0x0c1017,
    roughness: 0.92,
    metalness: 0.15,
  });
  const floorMat = new THREE.MeshStandardMaterial({
    color: 0x0b0e14,
    roughness: 0.4,
    metalness: 0.55,
  });
  const mullMat = new THREE.MeshStandardMaterial({
    color: 0x05070b,
    roughness: 0.6,
    metalness: 0.7,
  });

  const floor = new THREE.Mesh(new THREE.PlaneGeometry(W, D), floorMat);
  floor.rotation.x = -Math.PI / 2;
  floor.position.set(0, 0, CZ);
  group.add(floor);

  const ceil = new THREE.Mesh(new THREE.PlaneGeometry(W, D), wallMat);
  ceil.rotation.x = Math.PI / 2;
  ceil.position.set(0, H, CZ);
  group.add(ceil);

  const back = new THREE.Mesh(new THREE.PlaneGeometry(W, H), wallMat);
  back.position.set(0, H / 2, CZ - D / 2);
  group.add(back);

  // Windows on BOTH side walls: a sky that follows Israel time - day = light
  // horizon to deep blue with a sun, night = dark sky with twinkling stars.
  // During the day a volumetric light shaft spills through each window onto
  // the floor. Both windows share one texture set.
  const isDay = isDayInIsrael();
  const skyMats = [];
  const shafts = [];
  function makeSky(w, h, star, count, sunRays) {
    const mat = new THREE.MeshBasicMaterial({
      map: makeSkyTexture(THREE, w, h, isDay, star, count, sunRays).tex,
      toneMapped: false,
      color: 0xffffff,
    });
    skyMats.push({ mat, w, h, star, count, sunRays });
    return mat;
  }
  function addShafts(w, h, pos, rotY) {
    const shaft = makeLightShaft(THREE, H * 0.62);
    // Cone's +Y is the beam's axis; rotate it so it leans down and into the
    // room, its base (wide end) sitting at the window, its tip near the floor.
    shaft.rotation.x = Math.PI * 0.72;
    shaft.rotation.y = rotY;
    shaft.position.set(pos.x, pos.y - 0.15, pos.z + 0.15);
    shaft.visible = isDay;
    group.add(shaft);
    shafts.push(shaft);
  }
  // Twinkle overlays: one per window, only their opacity pulses per frame.
  const twinkleMats = [];
  function addTwinkle(w, h, pos, rotY) {
    const mat = new THREE.MeshBasicMaterial({
      map: makeTwinkleTexture(THREE, w, h, w > 5 ? 0.08 : 2.2, w > 5 ? 6 : 8),
      transparent: true,
      opacity: w > 5 ? 0.22 : 0.4,
      toneMapped: false,
      fog: false,
      depthWrite: false,
    });
    const plane = new THREE.Mesh(new THREE.PlaneGeometry(w, h), mat);
    plane.position.copy(pos);
    plane.rotation.y = rotY;
    group.add(plane);
    twinkleMats.push({
      mat,
      speed: 0.6 + Math.random() * 0.9,
      phase: Math.random() * Math.PI * 2,
    });
  }
  for (const sx of [-1, 1]) {
    // Seamless panoramic floor-to-ceiling side skyline windows spanning full room depth
    const skyW = D;
    const skyH = H * 0.96;
    const skyY = H * 0.5;
    const sky = new THREE.Mesh(
      new THREE.PlaneGeometry(skyW, skyH),
      makeSky(skyW, skyH, 0.35, 120, false),
    );
    // Plane faces +z by default; turn it to face INTO the room on its wall.
    sky.rotation.y = sx < 0 ? Math.PI / 2 : -Math.PI / 2;
    sky.position.set(sx * (W / 2 - 0.02), skyY, CZ);
    group.add(sky);
    addShafts(
      skyW,
      skyH,
      new THREE.Vector3(sx * (W / 2 - 0.1), skyY, CZ),
      sky.rotation.y,
    );
    addTwinkle(
      skyW,
      skyH,
      new THREE.Vector3(sx * (W / 2 - 0.04), skyY, CZ),
      sky.rotation.y,
    );
  }

  // Presentation screen on the back wall (clean architectural mounting, no heavy cage frames).
  const sFrame = new THREE.Mesh(new THREE.BoxGeometry(8.4, 3.5, 0.04), mullMat);
  sFrame.position.set(0, 2.8, CZ - D / 2 + 0.02);
  group.add(sFrame);
  const screen = new THREE.Mesh(
    new THREE.PlaneGeometry(8.2, 3.3),
    new THREE.MeshBasicMaterial({
      map: makeScreenTexture(THREE),
      toneMapped: false,
      color: 0xe8e8e8,
    }),
  );
  screen.position.set(0, 2.8, CZ - D / 2 + 0.06);
  group.add(screen);

  // Expansive panoramic skyline windows on the back wall, flanking the screen.
  // Enlarged to fill the back wall comfortably without obstructing bars/frames.
  for (const wx of [-1, 1]) {
    const winW = 6.8;
    const winH = H * 0.88;
    const winY = H * 0.5;
    const x = wx * (W / 4 + 1.8);
    const win = new THREE.Mesh(
      new THREE.PlaneGeometry(winW, winH),
      makeSky(winW, winH, 1.8, 35, true),
    );
    win.position.set(x, winY, CZ - D / 2 + 0.04);
    group.add(win);
    addTwinkle(winW, winH, new THREE.Vector3(x, winY, CZ - D / 2 + 0.06), 0);
  }

  // The deal table: long slab, two leg panels, one glowing centerline.
  const tableMat = new THREE.MeshStandardMaterial({
    color: 0x11151d,
    roughness: 0.35,
    metalness: 0.5,
  });
  const top = new THREE.Mesh(new THREE.BoxGeometry(9, 0.1, 2.2), tableMat);
  top.position.set(0, 1.0, CZ);
  group.add(top);
  for (const lx of [-3.6, 3.6]) {
    const leg = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.95, 1.8), tableMat);
    leg.position.set(lx, 0.48, CZ);
    group.add(leg);
  }
  const glow = new THREE.Mesh(
    new THREE.BoxGeometry(8.7, 0.012, 0.05),
    new THREE.MeshBasicMaterial({ color: ACCENT, toneMapped: false }),
  );
  glow.position.set(0, 1.056, CZ);
  group.add(glow);

  // Chairs + seated figures, three per side, slightly staggered.
  // Each seated figure also gets an articulated right arm that rests reaching
  // toward the table and, every ~20s, raises to point at the board behind the
  // presenter - alternating ~140° and ~100° lifts (audienceUpdate drives it).
  // ponytail: one arm per figure (the near one), enough to read as "audience".
  const AUD_CYCLE = 20; // seconds between each audience point
  const AUD_RAISE = 1.2; // seconds hand takes to rise
  const AUD_HOLD = 2.0; // seconds held pointing
  const AUD_LOWER = 1.2; // seconds hand takes to drop back
  const AUD_ANGLES = [140, 100]; // alternating point heights, degrees
  const chairMat = new THREE.MeshStandardMaterial({
    color: 0x0d1118,
    roughness: 0.7,
    metalness: 0.3,
  });
  const skin = new THREE.MeshStandardMaterial({
    color: 0x0a0d12,
    roughness: 0.95,
    metalness: 0.05,
  });
  const audience = [];
  for (const sx of [-3, 0, 3]) {
    for (const side of [-1, 1]) {
      const chair = new THREE.Group();
      const seat = new THREE.Mesh(
        new THREE.BoxGeometry(0.6, 0.07, 0.6),
        chairMat,
      );
      seat.position.y = 0.48;
      const backrest = new THREE.Mesh(
        new THREE.BoxGeometry(0.6, 0.75, 0.07),
        chairMat,
      );
      backrest.position.set(0, 0.86, 0.3);
      chair.add(seat, backrest);
      // Both rows stay in place, but every chair now faces the board (-z):
      // the front row already did, and the row closer to the board (behind
      // the table) is rotated from facing the table to facing the board.
      chair.rotation.y = 0;
      chair.position.set(sx + (side > 0 ? 0.35 : -0.35), 0, CZ + side * 1.65);
      group.add(chair);
      const fig = makeFigure(THREE, skin, true);
      fig.position.set(chair.position.x, 0, chair.position.z);
      group.add(fig);
      // Articulated arm: hangs from the shoulder, tilted toward the table so
      // the hand sits near the tabletop at rest. Rotation.x swings it up and
      // toward the board (-z); sign depends on which side of the table.
      const arm = new THREE.Group();
      arm.position.set(0.26, 1.15, 0); // seated right shoulder
      const upper = new THREE.Mesh(
        new THREE.CapsuleGeometry(0.06, 0.26, 4, 10),
        skin,
      );
      upper.position.y = -0.13; // hangs toward the table
      arm.add(upper);
      fig.add(arm);
      audience.push({
        arm,
        rest: 0.35,
        offset: (audience.length / 6) * AUD_CYCLE,
      });
    }
  }

  // One standing presenter by the screen, closing the deal. Its arm is
  // articulated so it can point at the board; presenterUpdate() walks it
  // back and forth across the board's width and raises the arm periodically.
  const presenter = makePresenter(THREE, skin);
  presenter.fig.position.set(0, 0, CZ - D / 2 + 1.6);
  group.add(presenter.fig);

  // Presenter motion: walk left↔right along the board, then STOP and hold a
  // long pointing gesture at the screen (like a teacher facing the class and
  // pointing back at the board), then resume walking. The walk clock only
  // advances during walk phases, so the pause is exact and the walk resumes
  // smoothly. Reduced motion: frozen in the middle, arm down.
  // ponytail: fixed timings (WALK_T / GESTURE_T); re-tune if it feels off.
  const WALK = 3.2; // half-width of the walk range (board is 4 wide)
  const WALK_T = 9; // seconds to cross the full width
  const GESTURE_T = 35; // seconds frozen + holding the point
  const CYCLE = WALK_T + GESTURE_T;
  function presenterUpdate(t, reduced) {
    if (reduced) {
      presenter.fig.position.x += (0 - presenter.fig.position.x) * 0.1;
      presenter.arm.rotation.z += (0 - presenter.arm.rotation.z) * 0.1;
      return;
    }
    const cycles = Math.floor(t / CYCLE);
    const phase = t % CYCLE;
    // Walking time accumulates only while walking - during the gesture phase
    // it clamps, so the walk target freezes where it stopped.
    const walkTime = cycles * WALK_T + Math.min(phase, WALK_T);
    const k = (walkTime % (2 * WALK_T)) / (2 * WALK_T); // 0..1 out and back
    const eased = 0.5 - 0.5 * Math.cos(k * Math.PI * 2); // smooth 0→1→0
    const targetX = (eased * 2 - 1) * WALK; // -WALK..WALK
    // Ease toward the target so the stop + resume drift to a halt instead of
    // snapping; the gesture phase keeps targetX constant, so the figure stays
    // put while gesturing and glides away again when the walk resumes.
    presenter.fig.position.x += (targetX - presenter.fig.position.x) * 0.12;
    // Gesture: raise the arm quickly to ~140° (well past horizontal - reads
    // as a strong sweep), HOLD it for most of the gesture, then lower it.
    // Rotation about Z swings the hanging arm sideways in the xy-plane. The
    // hand crosses the body toward the board's other side: when the presenter
    // stops on the right half (x>0) the arm sweeps left (-x), and vice versa.
    const g = Math.max(0, phase - WALK_T); // 0..GESTURE_T
    const RAISE = 1.5; // seconds to raise / lower the arm
    const MAX_LIFT = (140 * Math.PI) / 180; // ~2.44 rad
    let lift;
    if (g < RAISE) lift = (g / RAISE) * MAX_LIFT;
    else if (g > GESTURE_T - RAISE) lift = ((GESTURE_T - g) / RAISE) * MAX_LIFT;
    else lift = MAX_LIFT; // held sweeping
    presenter.arm.rotation.z = (presenter.fig.position.x >= 0 ? -1 : 1) * lift;
  }

  // Audience motion: each seated figure periodically (every ~20s, staggered
  // across the six seats) raises its hand and points at the board, alternating
  // between a high 140° and a flatter 100° point. At rest the arm just hangs
  // toward the table. Reduced motion: all arms settle to rest, nothing raises.
  function audienceUpdate(t, reduced) {
    for (let i = 0; i < audience.length; i++) {
      const a = audience[i];
      if (reduced) {
        a.arm.rotation.x += (a.rest - a.arm.rotation.x) * 0.1;
        continue;
      }
      const p = (t + a.offset) % AUD_CYCLE;
      const G = AUD_RAISE + AUD_HOLD + AUD_LOWER;
      const degrees =
        AUD_ANGLES[Math.floor((t + a.offset) / AUD_CYCLE) % AUD_ANGLES.length];
      const lift = (degrees * Math.PI) / 180;
      let target = a.rest;
      if (p < AUD_RAISE) target = a.rest + (p / AUD_RAISE) * (lift - a.rest);
      else if (p < AUD_RAISE + AUD_HOLD) target = lift;
      else if (p < G)
        target =
          lift - ((p - AUD_RAISE - AUD_HOLD) / AUD_LOWER) * (lift - a.rest);
      // Ease so the raise/lower sweeps instead of snapping.
      a.arm.rotation.x += (target - a.arm.rotation.x) * 0.2;
    }
  }

  // Table props: a few closed laptops / document slabs.
  const propMat = new THREE.MeshStandardMaterial({
    color: 0x05070b,
    roughness: 0.5,
    metalness: 0.4,
  });
  for (const [px, pz] of [
    [-2.2, -0.5],
    [0.8, 0.55],
    [2.6, -0.45],
  ]) {
    const prop = new THREE.Mesh(
      new THREE.BoxGeometry(0.55, 0.025, 0.38),
      propMat,
    );
    prop.position.set(px, 1.065, CZ + pz);
    prop.rotation.y = px;
    group.add(prop);
  }

  // --- Room dressing: plants, coffee cups with steam, water bottles --------
  // Makes the deal-room feel lived-in instead of a bare stage. Steam is a few
  // soft translucent blobs that propsUpdate() drifts upward each frame.
  const potMat = new THREE.MeshStandardMaterial({
    color: 0x6b4423,
    roughness: 0.85,
    metalness: 0.1,
  });
  const leafMat = new THREE.MeshStandardMaterial({
    color: 0x2e3a20,
    roughness: 0.9,
    metalness: 0.05,
  });
  const cupMat = new THREE.MeshStandardMaterial({
    color: 0x0a0d13,
    roughness: 0.4,
    metalness: 0.5,
  });

  // Big potted plants in BOTH far corners (behind the table, under the windows).
  function makeBigPlant() {
    const plant = new THREE.Group();
    const pot = new THREE.Mesh(
      new THREE.CylinderGeometry(0.5, 0.36, 0.55, 16),
      potMat,
    );
    pot.position.y = 0.28;
    plant.add(pot);
    const trunk = new THREE.Mesh(
      new THREE.CylinderGeometry(0.05, 0.08, 1.1, 8),
      leafMat,
    );
    trunk.position.y = 1.0;
    plant.add(trunk);
    for (const [lx, ly, lz, rs] of [
      [0, 1.7, 0, 0.5],
      [0.3, 1.45, 0.12, 0.34],
      [-0.28, 1.5, -0.08, 0.36],
      [0.08, 1.95, 0.1, 0.3],
      [-0.06, 1.55, 0.24, 0.32],
    ]) {
      const leaf = new THREE.Mesh(new THREE.SphereGeometry(rs, 10, 8), leafMat);
      leaf.position.set(lx, ly, lz);
      plant.add(leaf);
    }
    return plant;
  }
  for (const sx of [-1, 1]) {
    const plant = makeBigPlant();
    plant.position.set(sx * (W / 2 - 1.2), 0, CZ - D / 2 + 1.4);
    group.add(plant);
  }

  // Small potted plant on the table, left of center.
  const smallPlant = new THREE.Group();
  const smallPot = new THREE.Mesh(
    new THREE.CylinderGeometry(0.16, 0.12, 0.18, 12),
    potMat,
  );
  smallPot.position.y = 1.09;
  smallPlant.add(smallPot);
  for (const [lx, ly, lz, rs] of [
    [0, 1.35, 0, 0.12],
    [0.1, 1.25, 0.04, 0.09],
    [-0.1, 1.26, -0.05, 0.09],
  ]) {
    const leaf = new THREE.Mesh(new THREE.SphereGeometry(rs, 8, 6), leafMat);
    leaf.position.set(lx, ly, lz);
    smallPlant.add(leaf);
  }
  smallPlant.position.set(-1.6, 0, CZ + 0.1);
  group.add(smallPlant);

  // Two coffee cups with steam columns, placed where the near-side audience sit.
  const steamBlobs = [];
  for (const [cx, cz] of [
    [-3.1, 0.5],
    [3.1, 0.55],
  ]) {
    const cup = new THREE.Mesh(
      new THREE.CylinderGeometry(0.09, 0.065, 0.12, 12),
      cupMat,
    );
    cup.position.set(cx, 1.06, CZ + cz);
    group.add(cup);
    const steam = new THREE.Group();
    steam.position.set(cx, 1.18, CZ + cz);
    for (let b = 0; b < 3; b++) {
      const blob = new THREE.Mesh(
        new THREE.SphereGeometry(0.045, 8, 6),
        new THREE.MeshBasicMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: 0.35,
          toneMapped: false,
          fog: false,
        }),
      );
      blob.position.y = b * 0.12;
      steam.add(blob);
      steamBlobs.push({ blob, base: b * 0.12 });
    }
    group.add(steam);
  }

  // Two water bottles on the table, far corners of the audience side.
  for (const [wx, wz] of [
    [-2.9, -0.5],
    [2.9, -0.55],
  ]) {
    const bottle = new THREE.Mesh(
      new THREE.CylinderGeometry(0.06, 0.06, 0.34, 12),
      cupMat,
    );
    bottle.position.set(wx, 1.17, CZ + wz);
    group.add(bottle);
    const cap = new THREE.Mesh(
      new THREE.SphereGeometry(0.045, 8, 6),
      new THREE.MeshStandardMaterial({
        color: 0x1d2f45,
        roughness: 0.5,
        metalness: 0.3,
      }),
    );
    cap.position.set(wx, 1.36, CZ + wz);
    group.add(cap);
  }

  // Ceiling lights: a 3x3 grid of LED panels spread across the whole room
  // (not just the center), each with its own warm fill, + one strong center
  // downlight over the table. three.js uses physically-correct lights
  // (r155+), so intensity is in candela - 150+ is what reads as "bright".
  const fixtureMat = new THREE.MeshStandardMaterial({
    color: 0x0a0e14,
    roughness: 0.6,
    metalness: 0.5,
  });
  // fog:false so the panels read as bright beacons through the hall haze and
  // are visible from the main view down the center aisle.
  const panelMat = new THREE.MeshBasicMaterial({
    color: 0xf4f8fc,
    toneMapped: false,
    fog: false,
  });
  const panelGeo = new THREE.BoxGeometry(1.5, 0.03, 0.5);
  const panelLights = [];
  const panelAt = [];
  for (const lx of [-7, 0, 7]) {
    for (const lz of [CZ - 6, CZ, CZ + 6]) {
      panelAt.push([lx, lz]);
      const housing = new THREE.Mesh(
        new THREE.BoxGeometry(1.6, 0.09, 0.6),
        fixtureMat,
      );
      housing.position.set(lx, H - 0.12, lz);
      group.add(housing);
      const panel = new THREE.Mesh(panelGeo, panelMat);
      panel.position.set(lx, H - 0.17, lz);
      group.add(panel);
      // ponytail: 9 glowing panels but only 3 real lights (middle row) -
      // every PointLight taxes every lit fragment in the whole scene, and
      // three fills across the room read the same as nine.
      if (lz === CZ) {
        const fill = new THREE.PointLight(0xdfe8f0, 18, 16, 1.5);
        fill.position.set(lx, H - 0.35, lz);
        fill.visible = true; // gently illuminates through exterior windows
        group.add(fill);
        panelLights.push(fill);
      }
    }
  }

  // Strong ceiling downlight over the table - bright enough to fully lift the
  // boardroom out of the hall's dark mood. Target is a child of the group so
  // it tracks the table center in world space.
  const ceilLight = new THREE.SpotLight(0xffffff, 0, 40, 0.62, 0.5, 1.6);
  ceilLight.position.set(0, H - 0.4, CZ);
  ceilLight.visible = false; // hidden until the camera enters the room
  group.add(ceilLight);
  const ceilTarget = new THREE.Object3D();
  ceilTarget.position.set(0, 0.9, CZ);
  group.add(ceilTarget);
  ceilLight.target = ceilTarget;

  // Steam columns: each blob drifts up on a staggered loop and fades out at
  // the top, so the cups look hot. Light shafts show only during the day.
  // Star twinkle is opacity uniforms - no canvas work per frame. Reduced
  // motion: static steam, steady stars.
  let shaftLast = -1;
  function propsUpdate(t, reduced) {
    if (t - shaftLast > 5) {
      shaftLast = t;
      const day = isDayInIsrael();
      for (const s of shafts) s.visible = day;
    }
    for (const tw of twinkleMats) {
      tw.mat.opacity = isDayInIsrael()
        ? 0
        : reduced
          ? 0.35
          : 0.15 + 0.5 * (0.5 + 0.5 * Math.sin(t * tw.speed + tw.phase));
    }
    for (let i = 0; i < steamBlobs.length; i++) {
      const b = steamBlobs[i];
      if (reduced) {
        b.blob.material.opacity = 0.15;
        continue;
      }
      const cyc = (t * 0.8 + i * 0.37) % 1;
      b.blob.position.y = b.base + cyc * 0.5;
      b.blob.material.opacity = 0.35 * (1 - cyc);
    }
  }

  const anchor = new THREE.Object3D();
  anchor.position.set(0, 0.9, CZ);
  group.add(anchor);

  return {
    group,
    anchor,
    ceilLight,
    panelLights,
    panelMat,
    screen,
    presenterUpdate,
    audienceUpdate,
    propsUpdate,
  };
}
